import { Duration, Instant } from "@js-joda/core"
import type { MaybeRef } from "@vueuse/core"
import { unref } from "vue"
import { assignLoadable, type Loadable, useInterval, useWindowListener } from "vue-utils"

export function useStoreRefresh<T extends Record<string, Loadable<unknown>>>(
	stores: MaybeRef<T>,
	refreshTime: Duration
) {
	const lastRefreshTime = new Map<keyof T, Instant>()

	async function refreshStore<K extends keyof T, V extends T[K]>(storeId: K, store: V) {
		try {
			const result = await store.queryData()
			assignLoadable(store, {
				type: "done",
				result,
			})
		} catch (e) {
			console.error(e)
		}
	}

	function shouldRefresh<K extends keyof T, V extends T[K]>(storeId: K, store: V): boolean {
		if (store.type === "initial" || store.type === "loading") {
			return false
		}
		const now = Instant.now()
		const lastUpdate = lastRefreshTime.get(storeId)

		if (!lastUpdate) {
			lastRefreshTime.set(storeId, now)
			return false
		}

		return lastUpdate.isBefore(now.minus(refreshTime))
	}

	function updateStores() {
		const storesValue = unref(stores)
		for (const storeId in storesValue) {
			const store = storesValue[storeId]
			if (shouldRefresh(storeId, store)) {
				void refreshStore(storeId, store)
			}
		}
	}

	useInterval(refreshTime.toMillis(), updateStores)
	useWindowListener("visibilitychange", () => !document.hidden && updateStores())
}
